<template>
    <article>
        <h2>Credentials and permissions</h2>

        <h3>Create IAM policy</h3>

        <p>
            Aws-agent requires permissions to describe RDS instances, read their logs and read Enhanced Monitoring data from CloudWatch.
        </p>

        <p><var>MonitoringReadOnlyAccess</var></p>

        <CodeSnippet>
        <pre v-hl.json>
{
    "Version": "2012-10-17",
    "Statement": [
        {
            "Effect": "Allow",
            "Action": [
                "rds:DescribeDBInstances",
                "rds:DescribeDBLogFiles",
                "rds:DownloadDBLogFilePortion",
                "rds:ListTagsForResource",
                "elasticache:DescribeCacheClusters",
                "elasticache:ListTagsForResource"
            ],
            "Resource": [
                "*"
            ]
        },
        {
            "Effect": "Allow",
            "Action": [
                "logs:GetLogEvents"
            ],
            "Resource": [
                "arn:aws:logs:*:*:log-group:RDSOSMetrics:log-stream:*"
            ]
        }
    ]
}
        </pre>
        </CodeSnippet>

        <h3>Attach IAM policy</h3>

        <p>
            Aws-agent uses <a href="https://docs.aws.amazon.com/sdk-for-go/v1/developer-guide/configuring-sdk.html#specifying-credentials" target="_blank">default credential provider chain</a> to find AWS credentials.
        </p>

        <p>
            Here are the most popular options:
        </p>

        <ul>
            <li>
                create an IAM user with programmatic access, attach the policy to it and use <var>AWS_ACCESS_KEY_ID/AWS_SECRET_ACCESS_KEY</var> environment variables
            </li>
            <li>
                attach an IAM role with the policy to an EC2 instance where the agent will be run on
            </li>
            <li>
                attach the policy to a Kubernetes <a href="https://eksctl.io/usage/iamserviceaccounts/" target="_blank">service account</a> and assign it to the agent
            </li>
        </ul>

        <h2>RDS for Postgresql</h2>

        <p>
            The agent discovers RDS instances in the specified region and gathers OS, Postgres and log metrics for each of them.
        </p>

        <h3>Create a database role</h3>

        <CodeSnippet>
        <pre v-hl.sql>
create role &lt;USER&gt; with login password '&lt;PASSWORD&gt;';
grant pg_monitor to &lt;USER&gt;;
        </pre>
        </CodeSnippet>

        <p>IAM database authentication is coming soon.</p>

        <h3>Enable pg_stat_statements</h3>

        <CodeSnippet>
        <pre v-hl.sql>
create extension pg_stat_statements;
        </pre>
        </CodeSnippet>

        <p>
            The <var>pg_stat_statements</var> extension should be loaded via the <var>shared_preload_libraries</var> server setting.
        </p>

        <h2>Run</h2>

        <h3>Kubernetes</h3>

        <CodeSnippet>
        <pre v-hl.bash>
helm repo add coroot https://coroot.github.io/helm-charts
helm repo update
helm install --namespace coroot --create-namespace \
    --set aws.region=&lt;REGION&gt; \
    --set aws.key=&lt;KEY&gt; \
    --set aws.secret=&lt;SECRET&gt; \
    --set rds.user=&lt;USER&gt; \
    --set rds.password=&lt;PASSWORD&gt; \
    aws-agent coroot/aws-agent
        </pre>
        </CodeSnippet>

        <p>
            If you use <a href="https://github.com/prometheus-operator/prometheus-operator" target="_blank">Prometheus Operator</a>,
            you will also need to create a PodMonitor:
        </p>

        <CodeSnippet>
        <pre v-hl.bash>
helm install --set podMonitor=true ...
        </pre>
        </CodeSnippet>

        <p>
            Make sure the PodMonitor matches <var>podMonitorSelector</var> defined in your Prometheus:
        </p>

        <pre v-hl.yaml>
apiVersion: monitoring.coreos.com/v1
kind: Prometheus
...
spec:
  ...
  podMonitorNamespaceSelector: {}
  podMonitorSelector: {}
  ...
        </pre>

        <p>
            The special value <var>{}</var> allows Prometheus to watch all the PodMonitors from all namespaces.
        </p>

        <h3>Docker</h3>

        <CodeSnippet>
        <pre v-hl.bash>
docker run --detach --name coroot-aws-agent \
    -e AWS_REGION=&lt;REGION&gt; \
    -e AWS_ACCESS_KEY_ID=&lt;KEY&gt; \
    -e AWS_SECRET_ACCESS_KEY=&lt;SECRET&gt; \
    -e RDS_DB_USER=&lt;USER&gt; \
    -e RDS_DB_PASSWORD=&lt;PASSWORD&gt; \
    ghcr.io/coroot/coroot-aws-agent
        </pre>
        </CodeSnippet>

    </article>
</template>

<script>

export default {
}
</script>

<style scoped>
</style>
